import { curWeight, kmToSteps, walkMet } from "./energy";
import type { KayaState, ProgramBlock, TreadmillPhase, TreadmillProgram, TreadmillTotals } from "./types";

/**
 * Programs built into the user's own treadmill.
 *
 * The point is not to drive the machine — Kaya cannot. It is to know what the machine is doing, so
 * a session run on P7 logs its real distance instead of Kaya's assumption about a protocol that
 * was never run. That also makes it a trustworthy source for stride calibration, which an assumed
 * distance can never be (ADR-012).
 */

export const MIN_SPEED = 0.5;
export const MAX_SPEED = 25;
export const MAX_INCLINE = 20;

export function blockSecs(b: ProgramBlock): number {
  return Math.round(b.mins * 60);
}

export function programSecs(p: TreadmillProgram): number {
  return p.blocks.reduce((a, b) => a + blockSecs(b), 0);
}

export function programKm(p: TreadmillProgram): number {
  return p.blocks.reduce((a, b) => a + (b.speed * blockSecs(b)) / 3600, 0);
}

/**
 * Totals for a whole program. Incline raises the calorie cost but not the distance — the belt
 * travels the same either way, which is why overriding it to flat changes kcal and nothing else.
 */
export function programTotals(
  S: KayaState,
  p: TreadmillProgram,
  flat = false
): TreadmillTotals & { secs: number } {
  const w = curWeight(S);
  let kcal = 0;
  p.blocks.forEach((b) => {
    const hours = blockSecs(b) / 3600;
    kcal += walkMet(b.speed, flat ? 0 : b.incline) * w * hours;
  });
  const km = programKm(p);
  return { kcal: Math.round(kcal), km, steps: kmToSteps(S, km), secs: programSecs(p) };
}

/** Turns a program into phases the existing player can run, so nothing about it is special-cased. */
export function programToPhases(p: TreadmillProgram, flat = false): TreadmillPhase[] {
  const speeds = p.blocks.map((b) => b.speed);
  const lo = Math.min(...speeds);
  const hi = Math.max(...speeds);
  // A block counts as "brisk" in the top third of this program's own range, so the colour coding
  // reflects effort within the program rather than an absolute speed that means nothing here.
  const cut = lo + (hi - lo) * 0.66;

  return p.blocks.map((b, i) => ({
    l: `Block ${i + 1}/${p.blocks.length}`,
    s: b.speed,
    secs: blockSecs(b),
    c: hi === lo ? "easy" : b.speed >= cut ? "brisk" : "easy",
    inc: flat ? 0 : b.incline,
  }));
}

/** Rejects the shapes that would produce nonsense totals rather than storing them. */
export function validateProgram(p: TreadmillProgram): string | null {
  if (!p.name.trim()) return "Give the program a name";
  if (!p.blocks.length) return "Add at least one block";
  if (p.blocks.length > 60) return "That is more blocks than any program needs";
  for (const b of p.blocks) {
    if (!(b.mins > 0) || b.mins > 120) return "Each block needs a length between 0 and 120 minutes";
    if (!(b.speed >= MIN_SPEED) || b.speed > MAX_SPEED) return `Speed must be ${MIN_SPEED}–${MAX_SPEED} km/h`;
    if (b.incline < 0 || b.incline > MAX_INCLINE) return `Incline must be 0–${MAX_INCLINE}%`;
  }
  return null;
}

export function findProgram(S: KayaState, id: string): TreadmillProgram | null {
  return (S.profile.programs || []).find((p) => p.id === id) || null;
}
